import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { UploadDocumentType } from '@/constants/businessConstants'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { DocumentCategorySelect } from '@/components/form-fields'
import { fetchUserDocuments } from '../lib/userDocuments.slice'
import { useAppDispatch } from '@/utils/typeHelpers'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { RowTypes } from '../table/TableRows'
import { UserDocumentsTable } from '../table'
import { DocumentTabPane } from '.'
import { DocumentUploadDialog, Text } from '@/ui'
import {
  closeUploadModal,
  openUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import { TabCategoryKeys as ModalTypes } from '../lib/constants'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'
import { useAnalyticsTrack } from '@/features/amplitude'

const COLUMNS = [
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'description',
    title: 'Description',
  },
  {
    sortBy: 'tag',
    title: 'Form Type',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const Incorporation: DocumentTabPane = ({ documents, tableOptions }) => {
  const dispatch = useFeatureDispatch()
  const appDispatch = useAppDispatch()
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const { activeUploadModal, selectedDocument, isRefreshingAfterDelete } =
    useFeatureState()
  const [searchParams] = useSearchParams()
  const track = useAnalyticsTrack()
  const documentCategoryId = searchParams.get('documentCategoryId')
  const [userDocumentCategoryId, setUserDocumentCategoryId] = useState<
    number | null | undefined
  >(documentCategoryId ? Number(documentCategoryId) : null)

  useEffect(() => {
    if (!isRefreshing && !isRefreshingAfterDelete) {
      setFilteredDocuments(documents)
    } else if (isRefreshingAfterDelete) {
      // Clear table when deleting
      setFilteredDocuments([])
    }
  }, [documents, isRefreshing, isRefreshingAfterDelete])

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: 'incorporation',
    documentType: UploadDocumentType.INCORPORATION,
    userDocumentCategoryId:
      selectedDocument?.userDocumentCategoryId || userDocumentCategoryId,
    extraPayload: selectedDocument?.userDocumentCategoryId
      ? {
          userDocumentCategoryId: selectedDocument.userDocumentCategoryId,
        }
      : undefined,
    onUploadSuccess: async (_uploadedDocuments) => {
      // Clear the table immediately
      setIsRefreshing(true)
      setFilteredDocuments([])

      // Close modal
      dispatch?.(closeUploadModal())

      // Fetch fresh data
      await appDispatch(fetchUserDocuments(true, true))

      // Re-enable updates from props
      setIsRefreshing(false)
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
  })

  const handleDocumentsUpload = useCallback(
    async (documents: File[]) => {
      await uploadDocuments(documents)
    },
    [uploadDocuments]
  )

  const openModal = useCallback(() => {
    track('clicked document upload', {
      document_category: ModalTypes.INCORPORATION_DOCUMENTS,
      document_subcategory: '',
    })
    dispatch?.(openUploadModal(ModalTypes.INCORPORATION_DOCUMENTS))
  }, [dispatch, track])

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <Text>
          Share entity formation documents here, such as articles of
          incorporation, LLC approval, or Form 2553.
        </Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.INCORPORATION}
        tableOptions={tableOptions}
      />

      <DocumentUploadDialog
        isOpen={activeUploadModal === ModalTypes.INCORPORATION_DOCUMENTS}
        onOpenChange={(open) => {
          if (!open) {
            dispatch?.(closeUploadModal())
          }
        }}
        onClose={() => dispatch?.(closeUploadModal())}
        onUpload={handleDocumentsUpload}
        isUploadDisabled={isUploading}
        cardTitle="Incorporation Documents"
        cardDescription="Share entity formation documents here, such as articles of incorporation, LLC approval, or Form 2553."
      >
        {!selectedDocument && (
          <DocumentCategorySelect
            userDocumentCategoryId={userDocumentCategoryId}
            setUserDocumentCategoryId={setUserDocumentCategoryId}
            documentCategoryType={UploadDocumentType.INCORPORATION}
          />
        )}
      </DocumentUploadDialog>
    </>
  )
}

Incorporation.paneDescription =
  'Please use this section to upload any entity formation documents, such as articles of incorporation, LLC approval, S corporation Form 2553, etc.'
Incorporation.paneTitle = 'Incorporation'
